import { useMemo } from 'react';
import { ResponsiveLine, type LineSeries } from '@nivo/line';

import { lineTheme } from '@/components/Graphs/Charts/Line/LineHelpers';
import type { HistoricalScoreYearPoint } from '@/services/evidenceTimelineService';

type HistoricalScoreLineChartProps = {
  points: HistoricalScoreYearPoint[];
  loading?: boolean;
  rangeLabel?: string;
};

type ChartDatum = {
  x: string;
  y: number;
  rawScore?: number;
  rawPublications?: number;
};

const rootClass = 'flex h-full w-full flex-col';
const rootStyle = { minHeight: 'min(36vh, 300px)', minWidth: 240 } as const;
const plotClass = 'flex min-h-0 flex-1 flex-col w-full rounded-lg border border-slate-100 bg-white';
const labelStyle = { fontSize: '11px' } as const;

const SCORE_COLOR = '#be0080';
const PUBLICATIONS_COLOR = '#2563eb';

function buildYScale(values: number[]) {
  if (values.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.1, 0.02);
  return {
    min: Math.max(0, min - padding),
    max: max + padding,
  };
}

function mapPublicationsToScoreScale(
  publications: number,
  maxPublications: number,
  scoreScale: { min: number; max: number },
): number {
  if (maxPublications <= 0) return scoreScale.min;
  const span = scoreScale.max - scoreScale.min;
  return scoreScale.min + (publications / maxPublications) * span;
}

export function HistoricalScoreLineChart({
  points,
  loading = false,
  rangeLabel,
}: HistoricalScoreLineChartProps) {
  const scoreValues = useMemo(
    () =>
      points.map(p => p.score).filter((value): value is number => value != null && Number.isFinite(value)),
    [points],
  );

  const publicationValues = useMemo(
    () =>
      points
        .map(p => p.publications)
        .filter((value): value is number => value != null && Number.isFinite(value)),
    [points],
  );

  const scoreScale = useMemo(() => buildYScale(scoreValues), [scoreValues]);
  const publicationScale = useMemo(() => buildYScale(publicationValues), [publicationValues]);
  const plotScale = scoreValues.length > 0 ? scoreScale : publicationScale;
  const maxPublications = useMemo(
    () => (publicationValues.length > 0 ? Math.max(...publicationValues) : 0),
    [publicationValues],
  );

  const chartData = useMemo<LineSeries[]>(() => {
    const series: LineSeries[] = [];

    if (scoreValues.length > 0) {
      series.push({
        id: 'Score',
        data: points
          .filter(p => p.score != null && Number.isFinite(p.score))
          .map(
            (p): ChartDatum => ({
              x: String(p.year),
              y: p.score as number,
              rawScore: p.score as number,
            }),
          ),
      });
    }

    if (publicationValues.length > 0) {
      series.push({
        id: 'Publications',
        data: points
          .filter(p => p.publications != null && Number.isFinite(p.publications))
          .map((p): ChartDatum => {
            const publications = p.publications as number;
            const y =
              scoreValues.length > 0
                ? mapPublicationsToScoreScale(publications, maxPublications, scoreScale)
                : publications;
            return {
              x: String(p.year),
              y,
              rawPublications: publications,
            };
          }),
      });
    }

    return series;
  }, [maxPublications, points, publicationValues.length, scoreScale, scoreValues.length]);

  const hasChartData = chartData.some(series => series.data.length > 0);
  const showPublicationsAxis = publicationValues.length > 0 && scoreValues.length > 0;
  const scoreSpan = scoreScale.max - scoreScale.min;

  const header = (
    <div className="mb-2 flex shrink-0 items-baseline justify-between gap-2">
      <p className="font-semibold uppercase tracking-wide text-slate-500" style={labelStyle}>
        Score & publications by year
      </p>
      {rangeLabel ? (
        <p className="text-slate-400" style={labelStyle}>
          {rangeLabel}
        </p>
      ) : null}
    </div>
  );

  const legend = hasChartData ? (
    <div className="mb-2 flex shrink-0 flex-wrap gap-3">
      {scoreValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: SCORE_COLOR }} />
          <span className="text-xs text-slate-600">Annual Score</span>
        </div>
      ) : null}
      {publicationValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: PUBLICATIONS_COLOR }}
          />
          <span className="text-xs text-slate-600">Annual Publications</span>
        </div>
      ) : null}
    </div>
  ) : null;

  if (loading) {
    return (
      <div className={rootClass} style={rootStyle}>
        {header}
        <div
          className={`${plotClass} items-center justify-center border-dashed border-slate-200 bg-slate-50/60`}
        >
          <p className="text-sm text-gray-500">Loading chart…</p>
        </div>
      </div>
    );
  }

  if (!hasChartData) {
    return (
      <div className={rootClass} style={rootStyle}>
        {header}
        <div
          className={`${plotClass} items-center justify-center border-dashed border-slate-200 bg-slate-50/60 px-3 text-center`}
        >
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm font-medium text-gray-500">No score data for this range.</p>
            {rangeLabel ? <p className="text-xs text-gray-400">Range: {rangeLabel}</p> : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass} style={rootStyle}>
      {header}
      {legend}
      <div className={plotClass}>
        <ResponsiveLine
          data={chartData}
          margin={{
            top: 12,
            right: showPublicationsAxis ? 56 : 24,
            bottom: 56,
            left: 56,
          }}
          theme={lineTheme}
          colors={series => (series.id === 'Publications' ? PUBLICATIONS_COLOR : SCORE_COLOR)}
          lineWidth={2}
          curve="monotoneX"
          enablePoints
          pointSize={8}
          pointBorderWidth={2}
          pointBorderColor="#ffffff"
          useMesh
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: plotScale.min,
            max: plotScale.max,
            stacked: false,
          }}
          axisBottom={{
            tickSize: 4,
            tickPadding: 8,
            tickRotation: (chartData[0]?.data.length ?? 0) > 8 ? -65 : 0,
            legend: 'Year',
            legendOffset: 44,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 4,
            tickPadding: 8,
            legend: scoreValues.length > 0 ? 'Score' : 'Publications',
            legendOffset: -48,
            legendPosition: 'middle',
            format: (value: number) =>
              scoreValues.length > 0 ? value.toFixed(2) : String(Math.round(value)),
          }}
          axisRight={
            showPublicationsAxis
              ? {
                  tickSize: 4,
                  tickPadding: 8,
                  legend: 'Publications',
                  legendOffset: 48,
                  legendPosition: 'middle',
                  format: (value: number) => {
                    if (scoreSpan <= 0 || maxPublications <= 0) return '0';
                    const publications = ((value - scoreScale.min) / scoreSpan) * maxPublications;
                    return String(Math.round(publications));
                  },
                }
              : null
          }
          tooltip={({ point }) => {
            const datum = point.data as ChartDatum;

            return (
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md">
                <div className="font-semibold text-slate-800">{datum.x}</div>
                {datum.rawScore != null ? (
                  <div className="text-slate-600">Score: {datum.rawScore.toFixed(3)}</div>
                ) : null}
                {datum.rawPublications != null ? (
                  <div className="text-slate-600">Publications: {datum.rawPublications}</div>
                ) : null}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
